import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  GraduationCap,
  MailWarning,
  ShieldCheck,
  CheckCircle,
  ArrowRight,
  Brain,
  Target,
  Award,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: GraduationCap,
    title: 'Security Awareness Training',
    description:
      'Engaging, role-based training programmes that change behaviour, not just check compliance boxes.',
  },
  {
    icon: MailWarning,
    title: 'Phishing Simulations',
    description:
      'Realistic phishing campaigns that test and improve employee detection capabilities without blame.',
  },
  {
    icon: Target,
    title: 'Role-Based Training',
    description:
      'Specialised training for high-risk roles: executives, finance, IT, and customer-facing teams.',
  },
  {
    icon: Award,
    title: 'Security Champions Network',
    description:
      'Build internal advocacy by training enthusiastic employees to promote security within their teams.',
  },
];

const whenYouNeedIt = [
  'Concerned about human error leading to breaches',
  'Experienced phishing or social engineering incidents',
  'Need to meet training compliance requirements',
  'Security is seen as ITs problem, not everyones',
  'Employees bypass security controls for convenience',
  'Want to build a security-first culture',
  'High staff turnover requires ongoing training',
];

const cultureElements = [
  {
    title: 'Leadership Commitment',
    description:
      'Security starts at the top. Leaders model secure behaviour and prioritise safety.',
  },
  {
    title: 'Psychological Safety',
    description: 'Employees feel safe reporting mistakes without fear of blame or punishment.',
  },
  {
    title: 'Relevant Training',
    description: 'Training connects to real scenarios employees face in their daily work.',
  },
  {
    title: 'Positive Reinforcement',
    description: 'Good security behaviour is recognised and celebrated across the organisation.',
  },
  {
    title: 'Clear Communication',
    description: 'Security policies are understandable and accessible to everyone.',
  },
  {
    title: 'Continuous Improvement',
    description: 'The culture evolves based on feedback, incidents, and changing threats.',
  },
];

const metrics = [
  {
    metric: 'Phishing Click Rate',
    description: 'Percentage of employees clicking simulated phishing links',
    benchmark: 'Target: <5% (industry avg: 18%)',
  },
  {
    metric: 'Training Completion',
    description: 'Percentage of employees completing assigned training',
    benchmark: 'Target: >95%',
  },
  {
    metric: 'Report Rate',
    description: 'Employees reporting suspicious emails to security team',
    benchmark: 'Target: >80% of attacks reported',
  },
  {
    metric: 'Time to Report',
    description: 'Average time between receiving and reporting suspicious content',
    benchmark: 'Target: <15 minutes',
  },
];

export default function SecurityCulturePage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Security Culture Programmes',
    description:
      'Build a security-conscious culture where employees are your first line of defence',
    url: 'https://thecisonetwork.com/services/security-culture',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Security Culture Programmes"
        subtitle="Technology alone cannot protect you. Build a security-conscious culture where employees are your first line of defence."
        eyebrow="Our Services"
        backgroundImage={getServiceCard('security-culture')?.image}
        backgroundAlt={getServiceCard('security-culture')?.imageAlt}
        seoTitle="Security Culture & Awareness Training | The CISO Network"
        seoDescription="Build a strong security culture with awareness training, phishing simulations, and security champions programmes. Transform employees into your strongest defence."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Your People Are Your Strongest Defence
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  The vast majority of successful cyber attacks exploit human vulnerabilities, not
                  technical ones. Phishing, social engineering, and human error continue to be the
                  primary attack vectors - yet many organisations treat security as purely a
                  technical problem.
                </p>
                <p>
                  We help you build a security-conscious culture where employees understand their
                  role in protecting the organisation and feel empowered to act securely. Our
                  approach goes beyond{' '}
                  <Link to="/services/compliance" className="text-secondary hover:underline">
                    compliance
                  </Link>{' '}
                  checkbox training to create lasting behavioural change.
                </p>
                <p>
                  From engaging awareness programmes and realistic phishing simulations to{' '}
                  <Link to="/services/board-reporting" className="text-secondary hover:underline">
                    executive briefings
                  </Link>{' '}
                  and security champions networks, we provide everything you need to transform your
                  workforce into an active security asset.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Brain className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {whenYouNeedIt.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Culture & Awareness Services
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Comprehensive programmes that build lasting security awareness and behaviour
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Elements */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Elements of a Strong Security Culture
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Six foundational elements that define security-conscious organisations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cultureElements.map((element, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal/10 flex items-center justify-center mb-4">
                  <span className="text-teal font-bold text-xl">{i + 1}</span>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                  {element.title}
                </h3>
                <p className="text-muted-foreground text-sm">{element.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phishing Approach */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="text-2xl font-heading font-bold text-foreground mb-6">
                Our Phishing Simulation Approach
              </h3>
              <div className="space-y-4">
                {[
                  {
                    title: 'Baseline Assessment',
                    description:
                      'Start with a controlled campaign to understand your current vulnerability.',
                  },
                  {
                    title: 'Progressive Difficulty',
                    description:
                      'Gradually increase sophistication to continuously challenge employees.',
                  },
                  {
                    title: 'Immediate Education',
                    description:
                      'Those who click receive instant, helpful training rather than punishment.',
                  },
                  {
                    title: 'Positive Recognition',
                    description:
                      'Celebrate employees who identify and report phishing attempts correctly.',
                  },
                  {
                    title: 'Trend Analysis',
                    description:
                      'Track improvement over time and identify departments needing extra support.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-8 w-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-teal" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-1">
                        {item.title}
                      </h4>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2 bg-white rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  Measuring Culture Change
                </h3>
              </div>
              <p className="text-muted-foreground mb-6">
                We track meaningful metrics that demonstrate real security culture improvement, not
                just training completion.
              </p>
              <div className="space-y-4">
                {metrics.map((m, i) => (
                  <div key={i} className="p-4 bg-background rounded-xl">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-foreground">{m.metric}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{m.description}</p>
                    <p className="text-xs text-teal font-medium">{m.benchmark}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Topics */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
              Training Topics We Cover
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Phishing and social engineering',
                'Password security and MFA',
                'Data handling and classification',
                'Remote work security',
                'Mobile device security',
                'Safe web browsing',
                'Physical security awareness',
                'Incident reporting',
                'Vendor and supply chain risks',
                'AI and emerging threats',
                'Executive-specific risks',
                'GDPR and privacy essentials',
              ].map((topic, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-background rounded-xl">
                  <ShieldCheck className="h-5 w-5 text-teal flex-shrink-0" />
                  <span className="text-muted-foreground">{topic}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Transform Your Security Culture
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your security culture goals and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can help build a human firewall.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
